import type { ReactNode } from 'react';

import { BUSINESS } from '../../../constants/businessDetails';
import { FOOTER, PRACTICES, STATUS_LABEL } from './homepageContent';

/**
 * Landing footer — Product, Explore, Legal, Operator and Contact (brief, 2026-11-01), and the
 * statement that iAdvocate is not a law firm. Every policy page stays one click from the
 * public site and the registered business is named: Razorpay reviews the merchant's website
 * for Terms, Privacy, Cancellation & Refund, Shipping and Contact before it enables UPI
 * Autopay / cards / Subscriptions (2026-10-18).
 *
 * Plain <a> links, not router Links: the landing page is also rendered outside a router
 * (tests). A policy link names its standalone page, but a click on it slides the Trust Center
 * in over the page at that policy (TrustCenterSlide) — the footer never takes anyone away.
 */

const LINK =
  'text-[13px] text-chamber-muted hover:text-brass transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass focus-visible:outline-offset-2';

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h2 className="font-plex text-[10.5px] font-medium tracking-[0.24em] uppercase text-brass">{title}</h2>
      <ul className="mt-4 space-y-2.5">{children}</ul>
    </div>
  );
}

export default function LandingFooter() {
  return (
    <footer className="border-t border-brass-hairline px-5 sm:px-8 lg:px-14 pt-12 pb-8">
      <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
        <Group title={FOOTER.groups.product}>
          {PRACTICES.map((p) => (
            <li key={p.key}>
              <a href={`#practice-${p.key}`} className={LINK}>
                {p.name}
                {p.status !== 'beta_live' && ` — ${STATUS_LABEL[p.status]}`}
              </a>
            </li>
          ))}
        </Group>
        <Group title={FOOTER.groups.explore}>
          {FOOTER.explore.map((l) => (
            <li key={l.label}>
              <a href={l.href} className={LINK}>
                {l.label}
              </a>
            </li>
          ))}
        </Group>
        <Group title={FOOTER.groups.legal}>
          {FOOTER.legal.map((l) => (
            <li key={l.label}>
              <a href={l.href} className={LINK}>
                {l.label}
              </a>
            </li>
          ))}
        </Group>
        <Group title={FOOTER.groups.operator}>
          <li className="text-[13px] leading-[1.7] text-chamber-muted-dim">{FOOTER.operator}</li>
        </Group>
        <Group title={FOOTER.groups.contact}>
          {FOOTER.contacts.map((c) => (
            <li key={c.label} className="text-[13px] text-chamber-muted-dim">
              {c.label}:{' '}
              <a href={`mailto:${c.email}`} className={LINK}>
                {c.email}
              </a>
            </li>
          ))}
        </Group>
      </div>

      <p className="mt-12 max-w-[900px] text-[12.5px] leading-[1.7] text-chamber-muted-dim">{FOOTER.legalWording}</p>
      <p className="mt-4 font-instrument text-[12px] text-chamber-muted-dim">
        © {new Date().getFullYear()} {BUSINESS.legalName} · {BUSINESS.brand}
      </p>
    </footer>
  );
}
